import { Image } from '~/types/global';
import { PriceDetails } from '~/types/hdapi';

export interface ContentResponse<T = unknown> {
  readonly componentName: string;
  readonly name: string;
  readonly properties: T;
  readonly meta?: any;
  readonly path: string;
}

export interface AllResponse {
  contentType: string;
  properties: {
    [key: string]: any;
  };
}

export interface ApplicationUser {
  id: string;
  externalId: string; // This is the external id from the external system (SAP).
  firstName: string;
  lastName: string;
  email: string;
  permissions: string[];
  catalogues: string[];
}

// This is also what we store in the cookie
export interface LoginResponse {
  token: string;
  user: ApplicationUser;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ApiResponse {
  isSuccess: boolean;
  errorMessage?: string;
  data: object;
}

///////////////////////////////////
// BASKET
///////////////////////////////////
export interface BasketItem {
  id: string;
  productId: number;
  productName: string;
  productOptions: Record<string, string>;
  price: {
    productNetPrice: number;
    surcharge: number;
    adjustments: Adjustment[];
    quantity: number;
    totalNetPrice: number;
    tax: number;
    totalGrossPrice: number;
    taxPercentage: number;
    discountPercentage: number;
    discountAmount: number;
    display: string;
    displayWithTax: string;
    currencyFormat: string;
  };
  quantity: number;
  image: Image;
  offer?: string;
  slug?: string;
  reason?: string;
  url?: string;
}

export interface Adjustment {
  reason: string;
  amount: number;
  display: string;
}

export interface BasketPrice {
  currencyFormat: string;
  tax: number;
  netPrice: number;
  grossPrice: number;
  taxPercentage: number;
  totalAdjustments: number;
  adjustments: Adjustment[];
  discountPercentage: number;
  discountAmount: number;
  surchargeAmount: number;
  display: string;
  displayWithTax: string;
}

export interface Basket {
  id: string;
  userId: string;
  storeId: string;
  items: BasketItem[];
  price: BasketPrice;
  containerId: string;
  productClassification: ProductClassification;
  properties: Record<string, string>;
}

export interface TotalPrice extends BasketPrice {}

export interface BasketContainer {
  baskets: Basket[];
  totalPrice: TotalPrice;
  userId: string;
  id: string;
  customerReferenceNumber: string;
  createdDate: string;
}

export enum BasketType {
  Main = 'Main',
  Samples = 'Samples',
  Spares = 'Spares'
}

export enum ProductClassification {
  Unclassified = 0,
  Wholesale = 1,
  MadeToMeasure = 2,
  Spares = 3,
  Samples = 4,
  Materials = 5
}

export interface AddToBasketRequest {
  productId: number;
  type: ProductClassification;
  quantity: number;
  properties: Record<string, string>;
}

export interface RemoveFromBasketRequest {
  productId: number;
  type: ProductClassification;
  quantity: number;
  properties: Record<string, string>;
}

export interface AllowedCountryRegion {
  countryId: string;
  regionId: string | null;
}

export interface ShippingProviderSettings {
  ShipToExpress: boolean;
  Description: string;
  ExcludedProducts: string;
  AlternateAddress: boolean;
  MaxItems: string;
  Enabled: string;
  Name: string;
  ProductTypes: string;
  SapCode: string;
  ExcludedRanges: string;
}

export interface ShippingMethodPrice {
  CountryId?: string;
  CurrencyId?: string;
  RegionId?: string;
  Value?: number;
}

export interface ShippingMethod {
  Alias: string;
  Name: string;
  Sku: string;
  ImageId: string | null;
  TaxClassId: string | null;
  CalculationMode: number;
  CalculationConfig: unknown | null;
  ShippingProviderAlias: string;
  ShippingProviderSettings: ShippingProviderSettings;
  AllowedCountryRegions: AllowedCountryRegion[];
  Prices: ShippingMethodPrice[];
  SortOrder: number;
  IsDeleted: boolean;
  StoreId: string;
  IsNew: boolean;
  Id: string;
}

interface AllowedUser {
  userId: string;
}

interface AllowedUserRole {
  role: 'admin' | 'editor' | 'sensitiveData' | 'translator' | 'writer';
}

export interface StoreConfiguration {
  baseCurrencyId: string;
  defaultLocationId: string;
  defaultCountryId: string;
  defaultTaxClassId: string;
  defaultOrderStatusId: null | string;
  errorOrderStatusId: string;
  measurementSystem: number;
  pricesIncludeTax: boolean;
  cookieTimeout: string;
  useCookies: boolean;
  giftCardCodeLength: number;
  giftCardDaysValid: number;
  giftCardCodeTemplate: null | string;
  giftCardPropertyAliases: string[];
  giftCardActivationMethod: number;
  giftCardActivationOrderStatusId: null | string;
  defaultGiftCardEmailTemplateId: string;
  confirmationEmailTemplateId: null | string;
  errorEmailTemplateId: null | string;
  cartNumberTemplate: string;
  orderNumberTemplate: string;
  orderRoundingMethod: number;
  allowedUsers: AllowedUser[];
  allowedUserRoles: AllowedUserRole[];
  productPropertyAliases: string[];
  productUniquenessPropertyAliases: string[];
  shareStockFromStoreId: null | string;
  alias: string;
  name: string;
  sortOrder: number;
  isDeleted: boolean;
  isNew: boolean;
  id: string;
}

export interface DealerAddress {}

export interface Address {
  address1: string;
  address2?: string;
  city: string;
  county: string;
  postcode: string;
}

export interface OrderData {
  containerId?: string;
  userId?: string;
  customerReference: string;
  wholesaleShippingAddress?: Address;
  madeToMeasureShippingAddress?: Address;
  billingAddress?: Address;
  shippingSameAsBilling: boolean;
  wholesaleShippingMethodId?: string;
  madeToMeasureShippingMethodId?: string;
}

export interface UpdateQuantityRequest {
  orderLineId: string;
  type: string;
  quantity: number;
}

export interface SetItemReasonRequest {
  orderLineId: string;
  type: string;
  reason: number;
}

export interface EditBasketItemRequest {
  productId: number;
  type: ProductClassification;
  quantity: number;
  properties: Record<string, string>;
  orderLineId: string;
}

export type EditBasketItemRequestWithBasketType = EditBasketItemRequest & {
  basketType: BasketType;
};

export type ContentResponseWithAncestors<T = unknown> = ContentResponse<T> & {
  ancestors?: ContentResponse[];
};

export const SPARES_REASON_CODE_PROPERTY_NAME = 'reason';
export const CLASSIFICATION_PROPERTY_NAME = 'classification';

export const HIDDEN_PROPERTY_NAMES = [SPARES_REASON_CODE_PROPERTY_NAME, CLASSIFICATION_PROPERTY_NAME];

///////////////////////////////////
// ORDER HISTORY
///////////////////////////////////

export interface ProductItem {
  colourway: string;
  containerref: string;
  couriername: string;
  currency: string;
  deliverydate: string;
  invoice: string;
  itemstatus: string;
  itemvalue: string;
  productcode: string;
  productdesc: string;
  productline: string;
  qty: string;
  trackingnumber: string;
  trackingurl: string;
}

export interface Order {
  ponumber: string;
  ordernumber: string;
  createddate: string;
  orderstatus: string;
  reqdeliverydate: string;
  ordervalue: string;
  ordercurrency: string;
  products: ProductItem[];
}

///////////////////////////////////
// PRICE
///////////////////////////////////
export interface ProductPrice {
  price: PriceDetails;
}

export interface DealerContent {
  sapAccountNumber: string | null;
  paymentTerms: string | null;
  logoHelpText: unknown;
  hideArenaLogo: boolean;
  hideEclipseLogo: boolean;
  productClassificationExclusions: string[];
  productTypeExclusions: string[];
  excludedRanges: string | null;
  excludedProducts: string | null;
  excludedWholesaleSKUs: string | null;
}

export interface BasketCount {
  main: number;
  samples: number;
}
